import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MemberDashboard from './pages/MemberDashboard';
import TournamentList from './pages/TournamentList';
import MemberManagement from './pages/MemberManagement';
import MemberDetails from './pages/MemberDetails';
import TeeSheetEditor from './pages/TeeSheetEditor';
import TournamentSetup from './pages/TournamentSetup';
import TournamentDataImport from './pages/TournamentDataImport';
import RequestReview from './pages/RequestReview';
import PublicTeeSheetView from './pages/PublicTeeSheetView';
import WalletManagement from './pages/WalletManagement';
import WalletCreate from './pages/WalletCreate';
import WalletDetails from './pages/WalletDetails';
import WalletTransaction from './pages/WalletTransaction';
import Communication from './pages/Communication';
import EmailActivity from './pages/EmailActivity';
import ScheduledEmails from './pages/ScheduledEmails';
import ScheduledEmailEdit from './pages/ScheduledEmailEdit';
import NotificationListPage from './pages/NotificationListPage';
import TournamentLayout from './components/Tournament/TournamentLayout';
import TournamentRoster from './pages/TournamentRoster';
import TournamentContests from './pages/TournamentContests';
import TournamentFeedback from './pages/TournamentFeedback';
import TournamentLastMinuteInvites from './pages/TournamentLastMinuteInvites';
import TournamentRules from './pages/TournamentRules';
import TournamentWaitlist from './pages/TournamentWaitlist';
import PaceOfPlayManagement from './pages/PaceOfPlayManagement';
import LastMinuteInviteTest from './pages/LastMinuteInviteTest';
import RulesChat from './pages/RulesChat';
import Calendar from './pages/Calendar';
import EventDetail from './pages/EventDetail';
import CronDashboard from './pages/CronDashboard';
// Member Portal Pages
import PortalDashboard from './pages/Portal/PortalDashboard';
import ProfileEdit from './pages/Portal/ProfileEdit';
import PreferencesEdit from './pages/Portal/PreferencesEdit';
import CommunicationSettings from './pages/Portal/CommunicationSettings';
import LastMinuteSettings from './pages/Portal/LastMinuteSettings';
import PortalWallet from './pages/Portal/PortalWallet';
import PortalMessages from './pages/Portal/PortalMessages';
// Portal Tournament Pages
import PortalTournamentList from './pages/Portal/PortalTournamentList';
import PortalTournamentDetail from './pages/Portal/PortalTournamentDetail';
import PortalActivityTab from './pages/Portal/tabs/PortalActivityTab';
import PortalRosterRequestTab from './pages/Portal/tabs/PortalRosterRequestTab';
import PortalTeeSheetTab from './pages/Portal/tabs/PortalTeeSheetTab';
import PortalSideActionTab from './pages/Portal/tabs/PortalSideActionTab';
import PortalRecapTab from './pages/Portal/tabs/PortalRecapTab';
import PortalFeedbackTab from './pages/Portal/tabs/PortalFeedbackTab';
// TD Portal Management
import TournamentPortalManager from './pages/TournamentPortalManager';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <main>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/tournament/:tournamentId/teesheet/view" element={<PublicTeeSheetView />} />
              <Route path="/rules" element={<RulesChat />} />

              {/* Protected Routes - Role-based routing */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <RoleBasedDashboard />
                </ProtectedRoute>
              } />
              
              {/* Tournament Director Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute requireTD={true}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute requireTD={true}>
                  <Calendar />
                </ProtectedRoute>
              } />
              <Route path="/calendar/events/new" element={
                <ProtectedRoute requireTD={true}>
                  <EventDetail />
                </ProtectedRoute>
              } />
              <Route path="/calendar/events/:eventId" element={
                <ProtectedRoute requireTD={true}>
                  <EventDetail />
                </ProtectedRoute>
              } />
              <Route path="/tournaments" element={
                <ProtectedRoute requireTD={true}>
                  <TournamentList />
                </ProtectedRoute>
              } />
              <Route path="/members" element={
                <ProtectedRoute requireTD={true}>
                  <MemberManagement />
                </ProtectedRoute>
              } />
              <Route path="/members/:memberId" element={
                <ProtectedRoute requireTD={true}>
                  <MemberDetails />
                </ProtectedRoute>
              } />
              <Route path="/setup" element={
                <ProtectedRoute requireTD={true}>
                  <TournamentSetup />
                </ProtectedRoute>
              } />
              <Route path="/tournament/:tournamentId/import" element={
                <ProtectedRoute requireTD={true}>
                  <TournamentDataImport />
                </ProtectedRoute>
              } />
              <Route path="/tournament/:tournamentId/review" element={
                <ProtectedRoute requireTD={true}>
                  <RequestReview />
                </ProtectedRoute>
              } />
              
              {/* Tournament Management Routes (TD only) */}
              <Route path="/editor/:tournamentId" element={
                <ProtectedRoute requireTD={true}>
                  <TournamentLayout />
                </ProtectedRoute>
              }>
                <Route index element={<TeeSheetEditor />} />
                <Route path="roster" element={<TournamentRoster />} />
                <Route path="rules" element={<TournamentRules />} />
                <Route path="waitlist" element={<TournamentWaitlist />} />
                <Route path="last-minute-invites" element={<TournamentLastMinuteInvites />} />
                <Route path="contests" element={<TournamentContests />} />
                <Route path="feedback" element={<TournamentFeedback />} />
                <Route path="portal" element={<TournamentPortalManager />} />
              </Route>
              
              <Route path="/wallets" element={
                <ProtectedRoute requireTD={true}>
                  <WalletManagement />
                </ProtectedRoute>
              } />
              <Route path="/wallets/create" element={
                <ProtectedRoute requireTD={true}>
                  <WalletCreate />
                </ProtectedRoute>
              } />
              <Route path="/wallets/transfer" element={
                <ProtectedRoute requireTD={true}>
                  <WalletTransaction />
                </ProtectedRoute>
              } />
              <Route path="/pace-management" element={
                <ProtectedRoute requireTD={true}>
                  <PaceOfPlayManagement />
                </ProtectedRoute>
              } />
              <Route path="/communication" element={
                <ProtectedRoute requireTD={true}>
                  <Communication />
                </ProtectedRoute>
              } />
              <Route path="/email-activity" element={
                <ProtectedRoute requireTD={true}>
                  <EmailActivity />
                </ProtectedRoute>
              } />
              <Route path="/scheduled-emails" element={
                <ProtectedRoute requireTD={true}>
                  <ScheduledEmails />
                </ProtectedRoute>
              } />
              <Route path="/scheduled-emails/edit/:notificationId" element={
                <ProtectedRoute requireTD={true}>
                  <ScheduledEmailEdit />
                </ProtectedRoute>
              } />
              <Route path="/notification-list" element={
                <ProtectedRoute requireTD={true}>
                  <NotificationListPage />
                </ProtectedRoute>
              } />
              <Route path="/cron-dashboard" element={
                <ProtectedRoute requireTD={true}>
                  <CronDashboard />
                </ProtectedRoute>
              } />
              <Route path="/last-minute-invites/test" element={
                <ProtectedRoute>
                  <LastMinuteInviteTest />
                </ProtectedRoute>
              } />
              
              {/* Member Routes */}
              <Route path="/member-dashboard" element={
                <ProtectedRoute requireMember={true}>
                  <MemberDashboard />
                </ProtectedRoute>
              } />

              {/* Member Portal Routes */}
              <Route path="/portal" element={
                <ProtectedRoute requireMember={true}>
                  <PortalDashboard />
                </ProtectedRoute>
              } />
              <Route path="/portal/profile" element={
                <ProtectedRoute requireMember={true}>
                  <ProfileEdit />
                </ProtectedRoute>
              } />
              <Route path="/portal/preferences" element={
                <ProtectedRoute requireMember={true}>
                  <PreferencesEdit />
                </ProtectedRoute>
              } />
              <Route path="/portal/communication" element={
                <ProtectedRoute requireMember={true}>
                  <CommunicationSettings />
                </ProtectedRoute>
              } />
              <Route path="/portal/last-minute" element={
                <ProtectedRoute requireMember={true}>
                  <LastMinuteSettings />
                </ProtectedRoute>
              } />
              <Route path="/portal/wallet" element={
                <ProtectedRoute requireMember={true}>
                  <PortalWallet />
                </ProtectedRoute>
              } />
              <Route path="/portal/messages" element={
                <ProtectedRoute requireMember={true}>
                  <PortalMessages />
                </ProtectedRoute>
              } />
              <Route path="/portal/tournaments" element={
                <ProtectedRoute requireMember={true}>
                  <PortalTournamentList />
                </ProtectedRoute>
              } />
              <Route path="/portal/tournaments/:tournamentId" element={
                <ProtectedRoute requireMember={true}>
                  <PortalTournamentDetail />
                </ProtectedRoute>
              }>
                <Route index element={<PortalActivityTab />} />
                <Route path="activity" element={<PortalActivityTab />} />
                <Route path="request" element={<PortalRosterRequestTab />} />
                <Route path="teesheet" element={<PortalTeeSheetTab />} />
                <Route path="sideaction" element={<PortalSideActionTab />} />
                <Route path="recap" element={<PortalRecapTab />} />
                <Route path="feedback" element={<PortalFeedbackTab />} />
              </Route>
              
              {/* Shared Routes (Member can access own data, TD can access any) */}
              <Route path="/wallets/:walletId" element={
                <ProtectedRoute>
                  <WalletDetails />
                </ProtectedRoute>
              } />
              <Route path="/wallets/:walletId/:type" element={
                <ProtectedRoute>
                  <WalletTransaction />
                </ProtectedRoute>
              } />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
}

// Role-based dashboard component
const RoleBasedDashboard = () => {
  const { user, isTournamentDirector, isMember } = useAuth();
  
  if (isTournamentDirector()) {
    return <Navigate to="/dashboard" replace />;
  } else if (isMember()) {
    return <Navigate to="/portal" replace />;
  } else {
    return <div>Unknown user role</div>;
  }
};


export default App;